import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, Package, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const QuickStockSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      const response = await api.get(`/stock/locate?query=${encodeURIComponent(query)}`);
      setResults(response.data);
      
      if (response.data.locations_count === 0) {
        toast.warning('Articol fara stoc');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Articol negasit');
        setResults(null);
      } else {
        toast.error('Eroare la cautare');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const viewFullDetails = () => {
    navigate(`/wms/stock-locator?q=${encodeURIComponent(query)}`);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        title="Cautare Rapida Stoc"
      >
        <Search className="w-4 h-4 mr-2" />
        <span className="text-sm">Cauta Stoc Rapid</span>
      </Button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
          <div className="p-4 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 border-b">
              <h3 className="font-semibold text-slate-900">Cautare Rapida</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nume sau cod articol..."
                className="pl-10"
                autoFocus
              />
            </div>

            <Button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              className="w-full"
              size="sm"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cautare...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Cauta
                </>
              )}
            </Button>

            {/* Results */}
            {results && (
              <div className="pt-3 border-t space-y-3">
                {/* Article Info */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="font-semibold text-slate-900 text-sm">{results.article.name}</p>
                  <p className="text-xs text-slate-600 mt-1">
                    Cod: {results.article.code}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-600">Stoc Total:</span>
                    <span className="font-bold text-green-600">
                      {results.total_quantity} {results.article.unit}
                    </span>
                  </div>
                </div>

                {/* Locations Summary */}
                {results.locations.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-slate-700">
                      Gasit in {results.locations_count} locatii:
                    </p>
                    {results.locations.slice(0, 2).map((loc, idx) => (
                      <div key={idx} className="bg-slate-50 p-2 rounded text-xs space-y-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-slate-500" />
                          <span className="font-semibold">
                            {loc.cabinet} → {loc.shelf} → {loc.position}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="w-3 h-3 text-green-600" />
                          <span className="text-green-700 font-semibold">
                            {loc.quantity} {loc.unit}
                          </span>
                        </div>
                      </div>
                    ))}
                    {results.locations.length > 2 && (
                      <p className="text-xs text-slate-500 text-center">
                        +{results.locations.length - 2} mai multe locatii
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-orange-600 text-center py-2">
                    Articol fara stoc disponibil
                  </p>
                )}

                {/* View Full Details Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={viewFullDetails}
                  className="w-full"
                >
                  Vezi Detalii Complete
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickStockSearch;
